import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, ClickAwayListener, InputBase, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import { dateConverter, errorMessages, matchKorean } from '../../../utils/methods';
import { searchTickets, searchTicketTutor, getCoursePassSelectionOptions, getLockerPassSelectionOptions } from '../../../api/ticket';
import { getPointTrainers } from '../../../api/staff';
import { useQuery } from '@tanstack/react-query';
import { getMemberGroups } from '../../../api/member-group';
import CommonSearchSubmitButton from '../../../form-components/CommonSearchSubmitButton';
import CommonFilterActionButton from '../../../form-components/CommonFilterActionButton';
import CommonFilterToggle from '../../../form-components/CommonFilterToggle';
import {
  FilterContainer,
  FilterHeader,
  FilterTitle,
  CollapseIcon,
  FilterContent,
  SearchInputWrapper,
  SearchInput,
  FilterRowThree,
  FilterSectionFlex,
  FilterSectionFixed,
  SectionTitle,
  SectionTitleText,
  SectionTitleWithCheckbox,
  ChipsContainer,
  FilterChip,
  CheckboxContainer,
  CheckboxItem,
  StyledCheckbox,
  SmallCheckbox,
  RadioContainer,
  RadioItem,
  StyledRadio,
  StyledSelect,
  DatePickerContainer,
  DateSeparator,
  FilterFooter,
  CustomInputWrapper,
  CustomInput,
  CustomInputSuffix,
  EmptyStateMessage,
  FilterGroup,
  FilterGroupTitle,
  FilterGroupBody,
} from './MemberFilterStyled';
import { centersDetail } from '../../../api/store-management';
import CommonText from '../../../form-components/CommonText';
import LightVersionOverlay, { useTransientLightVersionOverlay } from '../../common/LightVersionOverlay';
import CountPeriodFilter from './CountPeriodFilter';
import useConsultOptions from '../../../hooks/useConsultOptions';

// 상품 필터 종류 — 개인·그룹은 같은 수강권 목록을 classType 으로 가른다(회원그룹 편집 모달과 동일).
const PRODUCT_FILTER_KINDS = [
  { value: 'PRIVATE', label: '개인' },
  { value: 'GROUP', label: '그룹' },
  { value: 'PASS', label: '기간' },
  { value: 'LOCKER', label: '라커' },
];

// Constants
const MEMBER_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'ACTIVE', label: '유효회원' },
  { value: 'SCHEDULED', label: '사용예정' },
  { value: 'EXPIRING_SOON', label: '만료임박' },
  { value: 'STOPPED', label: '중지회원' },
  { value: 'UNPAID', label: '미수회원' },
  { value: 'LOCKER_EXPIRING', label: '라커만료' },
  { value: 'EXPIRED', label: '만료회원' },
];

const TICKET_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'COURSE_TICKET', label: '수강권' },
  { value: 'COURSE_PASS', label: '이용권' },
  { value: 'TRIAL_TICKET', label: '일일권' },
  // 구독권 — 이 지점에서 지금 구독 중인 회원. 다른 회원권 유형과 같이 고르지 않는다(서버는 subscriptionActive 로 받음)
  { value: 'SUBSCRIPTION', label: '구독권' },
];

const SHARE_ROLE_OPTIONS = [
  { value: 'SHARING', label: '공유하는 회원' },
  { value: 'SHARED', label: '공유받는 회원' },
];

const REMAINING_COUNT_OPTIONS = [
  { value: 'UNDER_3', label: '3회 이하' },
  { value: 'UNDER_5', label: '5회 이하' },
  { value: 'CUSTOM', label: '직접입력' },
];

const GENDER_CHIPS = [
  { value: null, label: '전체' },
  { value: 'MALE', label: '남자' },
  { value: 'FEMALE', label: '여자' },
];

const AGE_RANGE_OPTIONS = [
  { value: '', label: '연령대를 선택해 주세요.' },
  { value: 'UNDER_30', label: '30세 미만' },
  { value: 'AGE_30_40', label: '30~40세' },
  { value: 'AGE_40_50', label: '40~50세' },
  { value: 'AGE_50_60', label: '50~60세' },
  { value: 'OVER_60', label: '60세 이상' },
];

const SMS_OPTIONS = [
  { value: null, label: '전체' },
  { value: true, label: '동의' },
  { value: false, label: '미동의' },
];

const APP_LINK_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'Y', label: '연동' },
  { value: 'N', label: '미연동' },
];

const SPORT_TYPE_LABELS = {
  FITNESS: '피트니스 / P.T',
  PILATES: '필라테스',
  BARRE_PILATES: '바레 필라테스',
  GOLF: '골프',
  YOGA: '요가',
  TANNING: '태닝',
  STRETCHING: '스트레칭',
};

// 묶음 접힘 상태 저장 키 — 기본값을 '접힘'으로 바꾸며(11/3) 키도 바꿨다(이전 키의 펼침 기록은 버린다)
const GROUP_OPEN_KEY = 'memberFilterGroupOpen.v2';
const GROUP_OPEN_DEFAULT = { member: false, ticket: false, visit: false, period: false };

// 접을 수 있는 묶음 제목 — 접힌 상태에서 켜 둔 조건이 있으면 '적용 중'으로 알린다
const CollapsibleGroupHeader = ({ title, open, active, onToggle }) => {
  const { t } = useTranslation();
  return (
    <Box
      onClick={onToggle}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', minHeight: '30px' }}>
      <FilterGroupTitle>{title}</FilterGroupTitle>
      {!open && active && (
        <Box
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#0F7F85',
            backgroundColor: '#E6F4F5',
            borderRadius: '10px',
            px: '8px',
            py: '1px',
          }}>
          {t('적용 중')}
        </Box>
      )}
      {/* 접기/펼치기 버튼 — 잘 보이게 테두리 있는 알약 버튼(제목 줄 어디를 눌러도 같은 동작) */}
      <Box
        sx={{
          ml: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          height: '30px',
          pl: '12px',
          pr: '8px',
          borderRadius: '999px',
          border: '1px solid #58B9C1',
          backgroundColor: open ? '#fff' : '#58B9C1',
          color: open ? '#0F7F85' : '#fff',
          fontSize: '13px',
          fontWeight: 600,
          transition: 'background-color 0.15s',
        }}>
        {open ? t('접기') : t('펼치기')}
        <KeyboardArrowDownIcon
          sx={{ fontSize: '20px', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        />
      </Box>
    </Box>
  );
};

const MemberFilter = ({
  initialQuery,
  submitHandler,
  query,
  setQuery,
  baseMemberTypes,
  setBaseMemberTypes,
  onClear,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { visitPaths } = useConsultOptions();
  const user = useSelector(state => state.user.user);

  const isGuestUser = user?.userRole === 'ROLE_GUEST';

  const liteOverlay = useTransientLightVersionOverlay();

  // State
  const [search, setSearch] = useState(query.searchInput || '');
  // 회원 상세 → 뒤로가기 시 펼침 상태 복원. 다른 메뉴에서 진입하면 MemberIndex가 키를 지워 기본(접힘)으로 시작
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('memberListFilterOpen') !== 'true');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [sportType, setSportType] = useState([]);

  // 회원 그룹 필터 옵션 — 그룹 관리 모달과 같은 쿼리 키를 써서
  // 모달에서 그룹을 만들거나 지우면 invalidate로 이 드롭다운도 즉시 갱신된다.
  const { data: memberGroups = [] } = useQuery({
    queryKey: ['memberGroups', user?.branch?.branchId],
    queryFn: () => getMemberGroups(user.token, user.branch.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 담당 강사 필터 옵션 — 판매등록과 같은 소스(지점 개인레슨 강사)
  const { data: assignedTrainerOptions = [] } = useQuery({
    queryKey: ['memberFilterTrainers', user?.branch?.branchId],
    queryFn: () => getPointTrainers(`${user.branch.branchId}&courseClassType=PRIVATE_LESSON`, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token,
  });

  // 보유 상품 픽커 — 판매등록 수강권 검색 모달과 같은 패턴(검색 + 카테고리 탭 + 목록 클릭 선택)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productCategory, setProductCategory] = useState('ALL');
  const [productSearch, setProductSearch] = useState('');
  const { data: coursePassOptions = [] } = useQuery({
    queryKey: ['memberFilterCoursePasses', user?.branch?.branchId],
    queryFn: () => getCoursePassSelectionOptions(user.token, user.branch.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token && productPickerOpen,
  });
  const { data: lockerPassOptions = [] } = useQuery({
    queryKey: ['memberFilterLockerPasses', user?.branch?.branchId],
    queryFn: () => getLockerPassSelectionOptions(user.token, user.branch.branchId),
    enabled: !!user?.branch?.branchId && !!user?.token && productPickerOpen,
  });

  // New filter states for UI
  const [memberExpireDateEnabled, setMemberExpireDateEnabled] = useState(false);
  const [lockerExpireDateEnabled, setLockerExpireDateEnabled] = useState(false);
  const [memberStopDateEnabled, setMemberStopDateEnabled] = useState(false);
  const [registeredDateEnabled, setRegisteredDateEnabled] = useState(false);
  // 묶음 접기 — 기본은 모두 접힘, 사람마다 기억(브라우저 저장소가 막히면 기본값)
  const [groupOpen, setGroupOpen] = useState(() => {
    try {
      return { ...GROUP_OPEN_DEFAULT, ...JSON.parse(localStorage.getItem(GROUP_OPEN_KEY) || '{}') };
    } catch (e) {
      return GROUP_OPEN_DEFAULT;
    }
  });
  const toggleGroup = key =>
    setGroupOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(GROUP_OPEN_KEY, JSON.stringify(next));
      } catch (e) {
        // 저장 못 해도 이번 화면에서는 접힌다
      }
      return next;
    });
  const [customRemainingCount, setCustomRemainingCount] = useState('');
  const [visitEnabled, setVisitEnabled] = useState(false);
  const [classAttendEnabled, setClassAttendEnabled] = useState(false); // 수업 출석 횟수 필터
  const [classAbsentEnabled, setClassAbsentEnabled] = useState(false); // 수업 결석(노쇼) 횟수 필터

  // query prop이 변경될 때 내부 state 동기화 (새로고침/초기화 시)
  useEffect(() => {
    setSearch(query.searchInput || '');
    setMemberExpireDateEnabled(!!query.ticketExpireDateFrom || !!query.ticketExpireDateTo);
    setLockerExpireDateEnabled(!!query.lockerExpireDateFrom || !!query.lockerExpireDateTo);
    setMemberStopDateEnabled(!!query.memberStopDateFrom || !!query.memberStopDateTo);
    setRegisteredDateEnabled(!!query.registeredDateFrom || !!query.registeredDateTo);
    setCustomRemainingCount(query.customRemainingCount || '');
    setVisitEnabled(!!query.visitCountCondition);
    setClassAttendEnabled(!!query.classAttendCountCondition);
    setClassAbsentEnabled(!!query.classAbsentCountCondition);
  }, [
    query.visitCountCondition,
    query.classAttendCountCondition,
    query.classAbsentCountCondition,
    query.searchInput,
    query.ticketExpireDateFrom,
    query.ticketExpireDateTo,
    query.lockerExpireDateFrom,
    query.lockerExpireDateTo,
    query.memberStopDateFrom,
    query.memberStopDateTo,
    query.registeredDateFrom,
    query.registeredDateTo,
    query.customRemainingCount,
  ]);

  // 라이트버전 사용자는 상세필터를 펼치는 대신 안내 오버레이를 잠깐 표시한다
  const handleToggleCollapse = () => {
    if (isGuestUser) {
      liteOverlay.show();
      return;
    }
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('memberListFilterOpen', next ? 'false' : 'true');
      return next;
    });
  };

  // 회원구분이 '전체'로 돌아가면 회원 만료일 필터를 비활성화하고 값도 정리한다.
  // (전체 + 만료일 기간은 결과가 비어 버리는 모호한 조합이라 입력 단계에서 차단)
  useEffect(() => {
    if (baseMemberTypes.length === 0 && (query.ticketExpireDateFrom || query.ticketExpireDateTo)) {
      setMemberExpireDateEnabled(false);
      setQuery(prev => ({ ...prev, ticketExpireDateFrom: null, ticketExpireDateTo: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMemberTypes]);

  // Effects
  useEffect(() => {
    if (user?.token && user?.branch?.branchId) {
      getTicketList(user?.branch?.branchId);
    }
  }, [user?.token, user?.branch?.branchId]);

  // 지점별 sportTypes 조회
  useEffect(() => {
    if (user?.token && user?.branch?.centerId) {
      centerInfo();
    }
  }, [user?.token, user?.branch?.centerId]);

  useEffect(() => {
    if (selectedTicket) {
      getTutorList(selectedTicket);
    }
  }, [selectedTicket]);

  // API Handlers
  const getTicketList = async id => {
    try {
      const res = await searchTickets(user.token, id);
      setTickets(res);
    } catch (err) {
      console.log(err);
      toast.error(errorMessages(err));
    }
  };

  const getTutorList = async id => {
    try {
      const res = await searchTicketTutor(user.token, id);
      setTutors(res);
    } catch (err) {
      console.log(err);
      toast.error(errorMessages(err));
    }
  };

  // 전 종류 상품 풀 — {kind, id, label}. 카테고리 탭과 검색어로 걸러 목록에 뿌린다.
  const allProductOptions = React.useMemo(() => {
    const course = (tickets || [])
      .filter(item => item.classType === 'PRIVATE_LESSON' || item.classType === 'GROUP_LESSON')
      .map(item => ({
        kind: item.classType === 'PRIVATE_LESSON' ? 'PRIVATE' : 'GROUP',
        id: item.courseTicketId,
        label: item.courseTicketName,
      }));
    const passes = (coursePassOptions || []).map(item => ({
      kind: 'PASS', id: item.coursePassId, label: item.coursePassName,
    }));
    const lockers = (lockerPassOptions || []).map(item => ({
      kind: 'LOCKER', id: item.lockerPassId, label: item.lockerPassName,
    }));
    return [...course, ...passes, ...lockers];
  }, [tickets, coursePassOptions, lockerPassOptions]);

  const visibleProductOptions = React.useMemo(() => {
    let list = allProductOptions;
    if (productCategory !== 'ALL') {
      list = list.filter(option => option.kind === productCategory);
    }
    if (productSearch.trim()) {
      list = list.filter(option => matchKorean(option.label || '', productSearch.trim()));
    }
    return list;
  }, [allProductOptions, productCategory, productSearch]);

  // 고른 상품을 종류별로 갈라 query 에 반영 — 서버는 그중 하나라도 가진 회원을 준다(OR)
  const applyProducts = products =>
    setQuery({
      ...query,
      products,
      courseTicketIds: products
        .filter(p => p.kind === 'PRIVATE' || p.kind === 'GROUP')
        .map(p => p.id),
      coursePassIds: products.filter(p => p.kind === 'PASS').map(p => p.id),
      lockerPassIds: products.filter(p => p.kind === 'LOCKER').map(p => p.id),
    });

  // 지점별 sportTypes 조회 (centerId로 조회)
  const centerInfo = async () => {
    try {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      const formattedSportTypes = (response?.branchDetailInfo?.sportTypes || []).map(type => ({
        label: SPORT_TYPE_LABELS[type] || type,
        value: type,
      }));
      setSportType([{ value: null, label: '전체' }, ...formattedSportTypes]);
    } catch (error) {
      console.log('centerInfo 함수 에러 : ' + error);
    }
  };

  // Event Handlers
  const handleSearchInput = event => {
    setSearch(event.target.value);
  };

  const handleSearch = () => {
    const searchWord = { ...query, memberTypes: baseMemberTypes, searchInput: search, page: 1 };
    submitHandler(searchWord);

    setTimeout(() => {
      const tableElement = document.getElementById('member-list-table');
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const handleMemberTypeClick = value => {
    // 회원구분은 createBox '검색 기준'(base)에만 반영. 실제 적용은 검색 버튼(handleApply)에서.
    if (value === null) {
      setBaseMemberTypes([]);
    } else {
      const newMemberTypes = baseMemberTypes.includes(value)
        ? baseMemberTypes.filter(t => t !== value)
        : [...baseMemberTypes, value];
      setBaseMemberTypes(newMemberTypes);
    }
  };

  const handleExerciseTypeClick = value => {
    if (value === null) {
      setQuery({ ...query, exerciseTypes: [] });
    } else {
      const current = query.exerciseTypes || [];
      const newExerciseTypes = current.includes(value) ? current.filter(t => t !== value) : [...current, value];
      setQuery({ ...query, exerciseTypes: newExerciseTypes });
    }
  };

  const handleTicketTypeClick = value => {
    if (value === null) {
      setQuery({ ...query, ticketTypes: [], classTypes: [], subscriptionActive: null });
    } else if (value === 'SUBSCRIPTION') {
      // 구독권은 다른 회원권 유형과 배타 — 고르면 나머지를 비우고, 다시 누르면 전체로
      const on = query.subscriptionActive !== 'Y';
      setQuery({ ...query, ticketTypes: [], classTypes: [], subscriptionActive: on ? 'Y' : null });
    } else {
      const currentTicketTypes = query.ticketTypes || [];
      const newTicketTypes = currentTicketTypes.includes(value)
        ? currentTicketTypes.filter(t => t !== value)
        : [...currentTicketTypes, value];
      // 회원권 유형이 "이용권만"으로 좁혀지면 수강권 유형 선택값은 의미가 없어지므로 정리
      const onlyCoursePass = newTicketTypes.length > 0 && newTicketTypes.every(t => t === 'COURSE_PASS');
      setQuery({
        ...query,
        ticketTypes: newTicketTypes,
        classTypes: onlyCoursePass ? [] : query.classTypes,
        subscriptionActive: null,
      });
    }
  };

  const handleClassTypeChange = type => {
    if (query.classTypes.includes(type)) {
      setQuery({
        ...query,
        classTypes: query.classTypes.filter(s => s !== type),
      });
    } else {
      setQuery({
        ...query,
        classTypes: [...query.classTypes, type],
      });
    }
  };

  const handleShareRoleChange = value => {
    const current = query.shareRoles || [];
    setQuery({
      ...query,
      shareRoles: current.includes(value) ? current.filter(v => v !== value) : [...current, value],
    });
  };

  const handleRemainingCountClick = value => {
    if (value !== 'CUSTOM') {
      setQuery({ ...query, remainingCounts: value, customRemainingCount: '' });
      setCustomRemainingCount('');
    } else {
      setQuery({ ...query, remainingCounts: value });
    }
  };

  const handleGenderClick = value => {
    if (value === null) {
      setQuery({ ...query, genders: [] });
    } else {
      const newGenders = query.genders.includes(value)
        ? query.genders.filter(g => g !== value)
        : [...query.genders, value];
      setQuery({ ...query, genders: newGenders });
    }
  };

  const handleAgeRangeChange = event => {
    const value = event.target.value;
    if (value === '') {
      setQuery({ ...query, ageRanges: [] });
    } else {
      setQuery({ ...query, ageRanges: [value] });
    }
  };

  const handleSmsChange = value => {
    if (value === null) {
      setQuery({ ...query, smsReceiveList: [] });
    } else {
      setQuery({ ...query, smsReceiveList: [value] });
    }
  };

  // 숫자 입력 정리 — 숫자만 남기고 선행 0 제거 ("0" 상태에서 1을 치면 "01"이 되던 불편 해소)
  const handleApply = () => {
    submitHandler({ ...query, memberTypes: baseMemberTypes, searchInput: search, page: 1 });
  };

  const handleClear = () => {
    submitHandler(initialQuery);
    setQuery(initialQuery);
    setBaseMemberTypes([]);
    setSearch('');
    setMemberExpireDateEnabled(false);
    setLockerExpireDateEnabled(false);
    setMemberStopDateEnabled(false);
    setRegisteredDateEnabled(false);
    setCustomRemainingCount('');
    setVisitEnabled(false);
    setProductPickerOpen(false);
    setProductSearch('');
    setProductCategory('ALL');
    // URL 파라미터 정리 (Header에서 검색한 경우)
    if (onClear) {
      onClear();
    }
  };

  // Helper functions
  const getMemberTypeActive = value => {
    if (value === null) {
      return baseMemberTypes.length === 0;
    }
    return baseMemberTypes.includes(value);
  };

  const getTicketTypeActive = value => {
    const ticketTypes = query.ticketTypes || [];
    const subscriptionOn = query.subscriptionActive === 'Y';
    if (value === 'SUBSCRIPTION') return subscriptionOn;
    if (value === null) {
      return ticketTypes.length === 0 && !subscriptionOn;
    }
    return ticketTypes.includes(value);
  };

  const getExerciseTypeActive = value => {
    const current = query.exerciseTypes || [];
    if (value === null) {
      return current.length === 0;
    }
    return current.includes(value);
  };

  const getGenderActive = value => {
    if (value === null) {
      return query.genders.length === 0;
    }
    return query.genders.includes(value);
  };

  const getSmsActive = value => {
    if (value === null) {
      return query.smsReceiveList.length === 0;
    }
    return query.smsReceiveList.includes(value);
  };

  // 수강권 유형(개인/그룹 레슨)은 회원권 유형이 "이용권만"·"일일권만"인 경우에는 의미가 없다
  // (전체=빈 배열일 때는 모든 가능성이 열려있으므로 노출)
  const ticketTypesSelected = query.ticketTypes || [];
  const canSelectClassType =
    query.subscriptionActive !== 'Y' &&
    (ticketTypesSelected.length === 0
    || ticketTypesSelected.some(t => t !== 'COURSE_PASS' && t !== 'TRIAL_TICKET'));

  // 접힌 묶음에 켜 둔 조건이 있는지 — 제목 옆 '적용 중' 표시용
  const hasValue = v => (Array.isArray(v) ? v.length > 0 : v != null && v !== '');
  const memberInfoActive =
    baseMemberTypes.length > 0 ||
    [query.genders, query.smsReceiveList, query.ageRanges, query.appLinked, query.memberGroupId, query.visitPath].some(
      hasValue,
    );
  const ticketActive =
    query.subscriptionActive === 'Y' ||
    [
      query.ticketTypes,
      query.classTypes,
      query.exerciseTypes,
      query.remainingCounts,
      query.assignedTrainerId,
      query.products,
      query.shareRoles,
    ].some(hasValue);

  // 누적 방문수 년월 선택지 — 올해 포함 최근 N개년

  // 회원 만료일 필터는 특정 회원구분 선택 시에만 의미가 있다.
  // '전체'에서는 만료일의 STATUS 의미(만료자=INACTIVE / 그 외=ACTIVE 만료예정)가 모호해 결과가 비어 버린다.
  const isMemberTypeSelected = baseMemberTypes.length > 0;
  // 만료회원 선택 시엔 '만료일', 그 외 구분에선 '만료예정일'로 라벨을 구분한다(백엔드 필터 STATUS 분기와 일치).
  const expireDateLabel = baseMemberTypes.includes('EXPIRED') ? t('회원 만료일') : t('만료예정일');

  // Render
  const filterContent = (
    <FilterContainer>
      {/* Header */}
      <FilterHeader>
        <FilterTitle>{t('상세검색 필터')}</FilterTitle>
        <SearchInputWrapper>
          <SearchInput
            size="small"
            placeholder={t('이름 or 전화번호를 입력해 주세요')}
            value={search}
            onChange={handleSearchInput}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <CommonSearchSubmitButton onClick={handleSearch}>{t('검색')}</CommonSearchSubmitButton>
        </SearchInputWrapper>
        {/* <CollapseIcon isCollapsed={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)} /> */}
      </FilterHeader>

      {/* Content */}
      <FilterContent isCollapsed={isCollapsed}>
        {/* 묶음별 배치(11/3) — 회원 정보 / 회원권 / 방문·출결 / 기간 */}
        <FilterGroup>
          <CollapsibleGroupHeader
            title={t('회원 정보')}
            open={groupOpen.member}
            active={memberInfoActive}
            onToggle={() => toggleGroup('member')}
          />
          <FilterGroupBody open={groupOpen.member}>
            <FilterRowThree>
              <FilterSectionFlex>
                <SectionTitle>
                  <SectionTitleText>{t('회원 구분')}</SectionTitleText>
                </SectionTitle>
                <ChipsContainer>
                  {MEMBER_TYPE_CHIPS.map(chip => (
                    <FilterChip
                      key={chip.value || 'all'}
                      isActive={getMemberTypeActive(chip.value)}
                      onClick={() => handleMemberTypeClick(chip.value)}>
                      {t(chip.label)}
                    </FilterChip>
                  ))}
                </ChipsContainer>
              </FilterSectionFlex>
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('성별')}</SectionTitleText>
                </SectionTitle>
                <ChipsContainer>
                  {GENDER_CHIPS.map(chip => (
                    <FilterChip
                      key={chip.value || 'all'}
                      isActive={getGenderActive(chip.value)}
                      onClick={() => handleGenderClick(chip.value)}>
                      {t(chip.label)}
                    </FilterChip>
                  ))}
                </ChipsContainer>
              </FilterSectionFixed>
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('SMS 동의 여부')}</SectionTitleText>
                </SectionTitle>
                <RadioContainer>
                  {SMS_OPTIONS.map(option => (
                    <RadioItem key={option.value === null ? 'all' : String(option.value)}>
                      <StyledRadio checked={getSmsActive(option.value)} onChange={() => handleSmsChange(option.value)} />
                      <CommonText variant="label">{t(option.label)}</CommonText>
                    </RadioItem>
                  ))}
                </RadioContainer>
              </FilterSectionFixed>
            </FilterRowThree>
            <FilterRowThree>
              <FilterSectionFixed sx={{ flexDirection: 'row', gap: '16px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
                  <SectionTitle>
                    <SectionTitleText>{t('연령대')}</SectionTitleText>
                  </SectionTitle>
                  <StyledSelect
                    displayEmpty
                    value={query.ageRanges.length > 0 ? query.ageRanges[0] : ''}
                    onChange={handleAgeRangeChange}>
                    {AGE_RANGE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value} disabled={option.value === ''}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </Box>
                {/* 앱 연동 — 로그인한 지점(오른쪽 위)에 연동된 회원인지. 다지점 이용권으로 쓸 수 있어도 연동한 건 아니다.
                    값이 짧아(전체/연동/미연동) 폭을 고정하고 남는 폭은 연령대 안내 문구가 쓴다 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '120px', flexShrink: 0 }}>
                  <SectionTitle>
                    <SectionTitleText>{t('앱 연동')}</SectionTitleText>
                  </SectionTitle>
                  <StyledSelect
                    displayEmpty
                    value={query.appLinked ?? ''}
                    onChange={e => setQuery({ ...query, appLinked: e.target.value === '' ? null : e.target.value })}>
                    {APP_LINK_OPTIONS.map(option => (
                      <MenuItem key={option.value || 'all'} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </Box>
              </FilterSectionFixed>
              <FilterSectionFlex>
                <SectionTitle>
                  <SectionTitleText>{t('회원 그룹')}</SectionTitleText>
                </SectionTitle>
                <StyledSelect
                  displayEmpty
                  value={query.memberGroupId ?? ''}
                  onChange={e =>
                    setQuery({ ...query, memberGroupId: e.target.value === '' ? null : e.target.value })
                  }>
                  <MenuItem value="">{t('전체')}</MenuItem>
                  {memberGroups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name} ({group.memberCount}
                      {t('명')})
                    </MenuItem>
                  ))}
                  {query.memberGroupId != null &&
                    !memberGroups.some(group => group.id === query.memberGroupId) && (
                      <MenuItem value={query.memberGroupId}>{t('(삭제된 그룹)')}</MenuItem>
                    )}
                </StyledSelect>
              </FilterSectionFlex>
              {/* 방문경로(member.VISIT_PATH) — 잠재회원 전환 시 승계되거나 회원 상세에서 지정한 값 */}
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('방문경로')}</SectionTitleText>
                </SectionTitle>
                <StyledSelect
                  displayEmpty
                  value={query.visitPath ?? ''}
                  onChange={e =>
                    setQuery({
                      ...query,
                      visitPath: e.target.value === '' ? null : e.target.value,
                    })
                  }>
                  <MenuItem value="">{t('전체')}</MenuItem>
                  {visitPaths.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FilterSectionFixed>
            </FilterRowThree>
          </FilterGroupBody>
        </FilterGroup>
        <FilterGroup>
          <CollapsibleGroupHeader
            title={t('회원권')}
            open={groupOpen.ticket}
            active={ticketActive}
            onToggle={() => toggleGroup('ticket')}
          />
          <FilterGroupBody open={groupOpen.ticket}>
            <FilterRowThree>
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('회원권 유형')}</SectionTitleText>
                </SectionTitle>
                <ChipsContainer>
                  {TICKET_TYPE_CHIPS.map(chip => (
                    <FilterChip
                      key={chip.value || 'all'}
                      isActive={getTicketTypeActive(chip.value)}
                      onClick={() => handleTicketTypeClick(chip.value)}>
                      {t(chip.label)}
                    </FilterChip>
                  ))}
                </ChipsContainer>
              </FilterSectionFixed>
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('수강권 유형')}</SectionTitleText>
                </SectionTitle>
                {canSelectClassType ? (
                  <CheckboxContainer>
                    <CheckboxItem>
                      <StyledCheckbox
                        checked={query.classTypes.includes('PRIVATE_LESSON')}
                        onChange={() => handleClassTypeChange('PRIVATE_LESSON')}
                      />
                      <CommonText variant="label">{t('개인레슨')}</CommonText>
                    </CheckboxItem>
                    <CheckboxItem>
                      <StyledCheckbox
                        checked={query.classTypes.includes('GROUP_LESSON')}
                        onChange={() => handleClassTypeChange('GROUP_LESSON')}
                      />
                      <CommonText variant="label">{t('그룹레슨')}</CommonText>
                    </CheckboxItem>
                    <CheckboxItem>
                      <StyledCheckbox
                        checked={query.classTypes.includes('DUET_LESSON')}
                        onChange={() => handleClassTypeChange('DUET_LESSON')}
                      />
                      <CommonText variant="label">{t('듀엣레슨')}</CommonText>
                    </CheckboxItem>
                  </CheckboxContainer>
                ) : (
                  <EmptyStateMessage>
                      {query.subscriptionActive === 'Y'
                        ? t('구독권은 수강권 유형을 고르지 않습니다')
                        : t('회원권 유형에서 이용권 외의 항목을 선택해 주세요')}
                    </EmptyStateMessage>
                )}
              </FilterSectionFixed>
              <FilterSectionFlex>
                <SectionTitle>
                  <SectionTitleText>{t('운동 종목')}</SectionTitleText>
                </SectionTitle>
                <ChipsContainer>
                  {sportType.map(chip => (
                    <FilterChip
                      key={chip.value || 'all'}
                      isActive={getExerciseTypeActive(chip.value)}
                      onClick={() => handleExerciseTypeClick(chip.value)}>
                      {t(chip.label)}
                    </FilterChip>
                  ))}
                </ChipsContainer>
              </FilterSectionFlex>
            </FilterRowThree>
            <FilterRowThree>
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('잔여 횟수')}</SectionTitleText>
                </SectionTitle>
                <RadioContainer>
                  {REMAINING_COUNT_OPTIONS.map(option => (
                    <RadioItem key={option.value}>
                      <StyledRadio
                        checked={query.remainingCounts === option.value}
                        onChange={() => handleRemainingCountClick(option.value)}
                      />
                      <CommonText variant="label">{t(option.label)}</CommonText>
                    </RadioItem>
                  ))}
                  <CustomInputWrapper>
                    <CustomInput
                      size="small"
                      value={customRemainingCount}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setCustomRemainingCount(val);
                        setQuery({ ...query, customRemainingCount: val });
                      }}
                      disabled={query.remainingCounts !== 'CUSTOM'}
                    />
                    <CustomInputSuffix>{t('회')}</CustomInputSuffix>
                  </CustomInputWrapper>
                </RadioContainer>
              </FilterSectionFixed>
              {/* 담당 직원 (#77) — 이 직원(강사)이 배정된 수강권 보유 회원만 */}
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('담당 직원')}</SectionTitleText>
                </SectionTitle>
                <StyledSelect
                  displayEmpty
                  value={query.assignedTrainerId ?? ''}
                  onChange={e =>
                    setQuery({
                      ...query,
                      assignedTrainerId: e.target.value === '' ? null : e.target.value,
                    })
                  }>
                  <MenuItem value="">{t('전체')}</MenuItem>
                  {assignedTrainerOptions.map(option => (
                    <MenuItem key={option.trainerId} value={option.trainerId}>
                      {option.trainerName}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FilterSectionFixed>
              {/* 보유 상품 — 검색·카테고리·목록 클릭 선택이 한 팝오버에(판매등록 수강권 검색과 동일 패턴) */}
              <FilterSectionFixed sx={{ position: 'relative' }}>
                <SectionTitle>
                  <SectionTitleText>{t('보유 상품')}</SectionTitleText>
                </SectionTitle>
                <Box
                  onClick={() => setProductPickerOpen(open => !open)}
                  sx={{
                    height: '40px', borderRadius: '8px', border: '1px solid #D9D9D9',
                    backgroundColor: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', px: '12px', cursor: 'pointer',
                    fontSize: '14px',
                    color: (query.products || []).length ? '#1f2329' : '#a6a6a6',
                  }}>
                  <span>
                    {(query.products || []).length
                      ? `${t('선택한 상품')} ${(query.products || []).length}${t('개')}`
                      : t('상품을 선택해 주세요')}
                  </span>
                  <img src="/images/icons/chevron-down.svg" alt="" style={{ width: '16px' }} />
                </Box>

                {productPickerOpen && (
                  <ClickAwayListener onClickAway={() => setProductPickerOpen(false)}>
                    <Box
                      sx={{
                        // 목록이 길어지면 아래 회원목록 sticky 테이블 헤더(zIndex 100) 위로 올라와야 가려지지 않는다
                        position: 'absolute', top: '76px', left: 0, right: 0, zIndex: 1300,
                        backgroundColor: '#fff', border: '1px solid #E0E0E0', borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', p: '12px',
                      }}>
                      <InputBase
                        autoFocus
                        fullWidth
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        placeholder={t('상품명 검색 (초성 가능)')}
                        sx={{
                          height: '36px', px: '10px', mb: '8px', fontSize: '13px',
                          border: '1px solid #E0E0E0', borderRadius: '8px',
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: '6px', mb: '8px', flexWrap: 'wrap' }}>
                        {[{ value: 'ALL', label: '전체' }, ...PRODUCT_FILTER_KINDS].map(kind => (
                          <Box
                            key={kind.value}
                            onClick={() => setProductCategory(kind.value)}
                            sx={{
                              px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px',
                              cursor: 'pointer', border: '1px solid',
                              borderColor: productCategory === kind.value ? '#0F7F85' : '#E0E0E0',
                              backgroundColor: productCategory === kind.value ? '#edf8f9' : '#fff',
                              color: productCategory === kind.value ? '#0F7F85' : '#666',
                              fontWeight: productCategory === kind.value ? 700 : 400,
                            }}>
                            {t(kind.label)}
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {visibleProductOptions.length === 0 && (
                          <Box sx={{ py: '18px', textAlign: 'center', fontSize: '13px', color: '#a6a6a6' }}>
                            {t('검색 결과가 없습니다')}
                          </Box>
                        )}
                        {visibleProductOptions.map(option => {
                          const selected = (query.products || []).some(
                            p => p.kind === option.kind && p.id === option.id,
                          );
                          return (
                            <Box
                              key={`${option.kind}-${option.id}`}
                              onClick={() =>
                                selected
                                  ? applyProducts(
                                      (query.products || []).filter(
                                        p => !(p.kind === option.kind && p.id === option.id),
                                      ),
                                    )
                                  : applyProducts([...(query.products || []), option])
                              }
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '10px', py: '8px', borderRadius: '8px', cursor: 'pointer',
                                fontSize: '13px',
                                backgroundColor: selected ? '#edf8f9' : 'transparent',
                                color: selected ? '#0F7F85' : '#1f2329',
                                fontWeight: selected ? 600 : 400,
                                '&:hover': { backgroundColor: selected ? '#e0f2f4' : '#f5f5f5' },
                              }}>
                              <span>{option.label}</span>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box
                                  sx={{
                                    fontSize: '11px', px: '6px', py: '1px', borderRadius: '3px',
                                    backgroundColor: '#f1f3f5', color: '#666',
                                  }}>
                                  {t(PRODUCT_FILTER_KINDS.find(k => k.value === option.kind)?.label || '')}
                                </Box>
                                {selected && <span style={{ fontWeight: 700 }}>✓</span>}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      {(query.products || []).length > 1 && (
                        <Box sx={{ mt: '6px', fontSize: '12px', color: '#a6a6a6' }}>
                          {t('고른 상품 중 하나라도 가진 회원이 나옵니다')}
                        </Box>
                      )}
                    </Box>
                  </ClickAwayListener>
                )}

                {(query.products || []).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(query.products || []).map(product => (
                      <Box
                        key={`${product.kind}-${product.id}`}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          px: '8px', py: '2px', borderRadius: '12px', fontSize: '12px',
                          backgroundColor: '#edf8f9', border: '1px solid #d0edf0', color: '#0F7F85',
                        }}>
                        <span style={{ fontWeight: 600 }}>
                          {t(PRODUCT_FILTER_KINDS.find(k => k.value === product.kind)?.label || '')}
                        </span>
                        <span>{product.label}</span>
                        <span
                          style={{ cursor: 'pointer', fontWeight: 700 }}
                          onClick={() =>
                            applyProducts(
                              (query.products || []).filter(
                                p => !(p.kind === product.kind && p.id === product.id),
                              ),
                            )
                          }>
                          ✕
                        </span>
                      </Box>
                    ))}
                  </Box>
                )}
              </FilterSectionFixed>
            </FilterRowThree>
            <FilterRowThree>
              {/* 수강권 공유(#161) — 지금 공유 중인 것만. 둘 다 고르면 둘 중 하나라도 해당하는 회원 */}
              <FilterSectionFixed>
                <SectionTitle>
                  <SectionTitleText>{t('수강권 공유')}</SectionTitleText>
                </SectionTitle>
                <CheckboxContainer>
                  {SHARE_ROLE_OPTIONS.map(option => (
                    <CheckboxItem key={option.value}>
                      <StyledCheckbox
                        checked={(query.shareRoles || []).includes(option.value)}
                        onChange={() => handleShareRoleChange(option.value)}
                      />
                      <CommonText variant="label">{t(option.label)}</CommonText>
                    </CheckboxItem>
                  ))}
                </CheckboxContainer>
              </FilterSectionFixed>
            </FilterRowThree>
          </FilterGroupBody>
        </FilterGroup>
        <FilterGroup>
          <CollapsibleGroupHeader
            title={t('방문·출결')}
            open={groupOpen.visit}
            active={visitEnabled || classAttendEnabled || classAbsentEnabled}
            onToggle={() => toggleGroup('visit')}
          />
          {/* 접어도 입력값이 남도록 숨기기만 한다(언마운트하지 않음) */}
          <FilterGroupBody open={groupOpen.visit}>
            <FilterRowThree>
              <CountPeriodFilter
                prefix="visit"
                title="누적 방문수"
                hint="센터 입장(게이트·키오스크·체크인) + 예약 수업 출석. 하루에 여러 번 와도 1회."
                enabled={visitEnabled}
                onToggle={setVisitEnabled}
                query={query}
                setQuery={setQuery}
              />
              <CountPeriodFilter
                prefix="classAttend"
                title="수업 출석"
                hint="예약된 수업에 출석 처리된 횟수(수업 단위). 수업 없이 센터만 들른 날은 세지 않습니다."
                enabled={classAttendEnabled}
                onToggle={setClassAttendEnabled}
                query={query}
                setQuery={setQuery}
                defaultCondition="GTE"
              />
              <CountPeriodFilter
                prefix="classAbsent"
                title="수업 결석"
                hint="예약된 수업에 결석 처리된 횟수(수업 단위). 취소된 예약은 세지 않습니다."
                enabled={classAbsentEnabled}
                onToggle={setClassAbsentEnabled}
                query={query}
                setQuery={setQuery}
                defaultCondition="GTE"
              />
            </FilterRowThree>
          </FilterGroupBody>
        </FilterGroup>
        <FilterGroup>
          <CollapsibleGroupHeader
            title={t('기간')}
            open={groupOpen.period}
            active={
              registeredDateEnabled || memberExpireDateEnabled || lockerExpireDateEnabled || memberStopDateEnabled
            }
            onToggle={() => toggleGroup('period')}
          />
          <FilterGroupBody open={groupOpen.period}>
            <FilterRowThree>
              {/* 등록일 — 목록 '등록일' 칸과 같은 값(이 지점 등록일, 없으면 이 지점 첫 결제일) */}
              <FilterSectionFixed>
                <SectionTitleWithCheckbox>
                  <SmallCheckbox
                    checked={registeredDateEnabled}
                    onChange={e => {
                      setRegisteredDateEnabled(e.target.checked);
                      if (e.target.checked) {
                        const today = dayjs().format('YYYY-MM-DD');
                        setQuery({ ...query, registeredDateFrom: today, registeredDateTo: today });
                      } else {
                        setQuery({ ...query, registeredDateFrom: null, registeredDateTo: null });
                      }
                    }}
                  />
                  <SectionTitleText>{t('등록일')}</SectionTitleText>
                </SectionTitleWithCheckbox>
                <DatePickerContainer>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.registeredDateFrom}
                    onChange={n => setQuery({ ...query, registeredDateFrom: dateConverter(n) })}
                    disabled={!registeredDateEnabled}
                    placeholder="시작일"
                  />
                  <DateSeparator>~</DateSeparator>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.registeredDateTo}
                    onChange={n => setQuery({ ...query, registeredDateTo: dateConverter(n) })}
                    minDate={query.registeredDateFrom}
                    disabled={!registeredDateEnabled}
                    placeholder="종료일"
                  />
                </DatePickerContainer>
              </FilterSectionFixed>
              <FilterSectionFixed>
                <SectionTitleWithCheckbox>
                  <SmallCheckbox
                    checked={memberExpireDateEnabled}
                    disabled={!isMemberTypeSelected}
                    onChange={e => {
                      setMemberExpireDateEnabled(e.target.checked);
                      if (e.target.checked) {
                        const today = dayjs().format('YYYY-MM-DD');
                        setQuery({ ...query, ticketExpireDateFrom: today, ticketExpireDateTo: today });
                      } else {
                        setQuery({ ...query, ticketExpireDateFrom: null, ticketExpireDateTo: null });
                      }
                    }}
                  />
                  <SectionTitleText>
                    {isMemberTypeSelected ? expireDateLabel : `${t('회원 만료일')} (회원구분 선택 시)`}
                  </SectionTitleText>
                </SectionTitleWithCheckbox>
                <DatePickerContainer>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.ticketExpireDateFrom}
                    onChange={n => setQuery({ ...query, ticketExpireDateFrom: dateConverter(n) })}
                    disabled={!memberExpireDateEnabled || !isMemberTypeSelected}
                    placeholder="시작일"
                  />
                  <CommonText>~</CommonText>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.ticketExpireDateTo}
                    onChange={n => setQuery({ ...query, ticketExpireDateTo: dateConverter(n) })}
                    minDate={query.ticketExpireDateFrom}
                    disabled={!memberExpireDateEnabled || !isMemberTypeSelected}
                    placeholder="종료일"
                  />
                </DatePickerContainer>
              </FilterSectionFixed>
              <FilterSectionFixed>
                <SectionTitleWithCheckbox>
                  <SmallCheckbox
                    checked={lockerExpireDateEnabled}
                    onChange={e => {
                      setLockerExpireDateEnabled(e.target.checked);
                      if (e.target.checked) {
                        const today = dayjs().format('YYYY-MM-DD');
                        setQuery({ ...query, lockerExpireDateFrom: today, lockerExpireDateTo: today });
                      } else {
                        setQuery({ ...query, lockerExpireDateFrom: null, lockerExpireDateTo: null });
                      }
                    }}
                  />
                  <SectionTitleText>{t('락커 만료일')}</SectionTitleText>
                </SectionTitleWithCheckbox>
                <DatePickerContainer>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.lockerExpireDateFrom}
                    onChange={n => setQuery({ ...query, lockerExpireDateFrom: dateConverter(n) })}
                    disabled={!lockerExpireDateEnabled}
                    placeholder="시작일"
                  />
                  <DateSeparator>~</DateSeparator>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.lockerExpireDateTo}
                    onChange={n => setQuery({ ...query, lockerExpireDateTo: dateConverter(n) })}
                    minDate={query.lockerExpireDateFrom}
                    disabled={!lockerExpireDateEnabled}
                    placeholder="종료일"
                  />
                </DatePickerContainer>
              </FilterSectionFixed>
            </FilterRowThree>
            <FilterRowThree>
              <FilterSectionFixed>
                <SectionTitleWithCheckbox>
                  <SmallCheckbox
                    checked={memberStopDateEnabled}
                    onChange={e => {
                      setMemberStopDateEnabled(e.target.checked);
                      if (e.target.checked) {
                        const today = dayjs().format('YYYY-MM-DD');
                        setQuery({ ...query, memberStopDateFrom: today, memberStopDateTo: today });
                      } else {
                        setQuery({ ...query, memberStopDateFrom: null, memberStopDateTo: null });
                      }
                    }}
                  />
                  <SectionTitleText>{t('회원 중지 종료일')}</SectionTitleText>
                </SectionTitleWithCheckbox>
                <DatePickerContainer>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.memberStopDateFrom}
                    onChange={n => setQuery({ ...query, memberStopDateFrom: dateConverter(n) })}
                    disabled={!memberStopDateEnabled}
                    placeholder="시작일"
                  />
                  <DateSeparator>~</DateSeparator>
                  <CommonDatePicker
                    sx={{ width: '160px' }}
                    value={query.memberStopDateTo}
                    onChange={n => setQuery({ ...query, memberStopDateTo: dateConverter(n) })}
                    minDate={query.memberStopDateFrom}
                    disabled={!memberStopDateEnabled}
                    placeholder="종료일"
                  />
                </DatePickerContainer>
              </FilterSectionFixed>
            </FilterRowThree>
          </FilterGroupBody>
        </FilterGroup>
        {/* Footer Buttons */}
        <FilterFooter>
          <CommonFilterActionButton variant="primary" onClick={handleApply}>
            {t('설정한 조건으로 회원검색')}
          </CommonFilterActionButton>
          <CommonFilterActionButton variant="secondary" onClick={handleClear}>
            {t('검색조건 초기화')}
          </CommonFilterActionButton>
        </FilterFooter>
      </FilterContent>
      <CommonFilterToggle variant="border" open={!isCollapsed} onClick={handleToggleCollapse} />
    </FilterContainer>
  );

  // 라이트버전 사용자는 상세필터 클릭 시 안내 오버레이가 잠깐 덮였다 사라진다
  if (isGuestUser) {
    return <LightVersionOverlay active={liteOverlay.active}>{filterContent}</LightVersionOverlay>;
  }

  return filterContent;
};

export default MemberFilter;
